"use client";
import { usePowerSearch } from "../contexts/power-search-context";
import Button from "react-bootstrap/Button";
import { useState } from "react";
import { useSelector } from "react-redux";
import { mixpanelTrack } from "shared/trackEvents/mixpanelTracking";
import { isOpenRemunerationPlan } from "shared/plans";
import {
  POWER_SEARCH_EVENT_NAMES,
  MIXPANEL_PROPS,
} from "shared/trackEvents/constants";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

const SourceOptionsFilter = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const currentUser = useSelector((state) => state.users.user);

  const {
    filterOptions, // Contains source_types array from Redux
    sourceTypes: selectedSourceTypes, // Current filter value from context state
    updateFilter,
    updateFilterArray,
  } = usePowerSearch();

  const availableSourceTypes = filterOptions.source_types || [];

  const handleSourceChange = (e) => {
    const { value, checked } = e.target;
    let newSourceTypes = [...selectedSourceTypes];

    if (checked) {
      newSourceTypes.push(value);

      // Track source option click
      const eventName = isOpenRemunerationPlan(currentUser)
        ? POWER_SEARCH_EVENT_NAMES.OR_POWER_SEARCH_FILTER_SOURCE_CLICKED
        : POWER_SEARCH_EVENT_NAMES.OD_POWER_SEARCH_FILTER_SOURCE_CLICKED;

      mixpanelTrack(eventName, {
        [MIXPANEL_PROPS.FILTER_OPTION]: value,
        userEmail: currentUser?.email,
      });
    } else {
      newSourceTypes = newSourceTypes.filter((item) => item !== value);
    }

    updateFilter("sourceTypes", newSourceTypes);

    // Logic from legacy _setSourceOptions to clear related job titles if a source is unchecked
    // This logic might be better placed in a useEffect within the context or SearchContainer
    // if it needs to react to changes in sourceTypes from multiple places.
    // For now, keeping it simple here.
    if (
      !newSourceTypes.includes("company") &&
      !newSourceTypes.includes("private_company")
    ) {
      updateFilter("companyJobTitles", []);
      updateFilter("majorCompanyJobTitles", []);
      updateFilter("minorCompanyJobTitles", []);
    }
    if (!newSourceTypes.includes("superannuation")) {
      updateFilter("superannuationJobTitles", []);
      updateFilter("majorSuperannuationJobTitles", []);
      updateFilter("minorSuperannuationJobTitles", []);
    }
    // Add similar clear logic for government, other_government, nfp if needed
    if (!newSourceTypes.includes("government")) {
      updateFilter("governmentJobTitles", []);
      updateFilter("majorGovernmentJobTitles", []);
      updateFilter("minorGovernmentJobTitles", []);
    }
    if (!newSourceTypes.includes("other_government")) {
      updateFilter("otherGovernmentJobTitles", []);
      updateFilter("majorOtherGovernmentJobTitles", []);
      updateFilter("minorOtherGovernmentJobTitles", []);
    }
    if (!newSourceTypes.includes("nfp")) {
      updateFilter("nfpJobTitles", []);
      updateFilter("majorNfpJobTitles", []);
      updateFilter("minorNfpJobTitles", []);
    }
  };

  const handleSelectAll = () => {
    if (selectedSourceTypes.length === availableSourceTypes.length) {
      updateFilterArray("sourceTypes", []);
    } else {
      updateFilterArray(
        "sourceTypes",
        availableSourceTypes.map((option) => option.value)
      );
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between">
        <div
          className="d-flex align-items-center"
          style={{ cursor: "pointer" }}
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          <Button
            variant="link"
            size="sm"
            style={{
              display: "inline-block",
              padding: 0,
              margin: 0,
              textDecoration: "none",
              marginRight: "4px",
              focus: "none",
              outline: "none",
              border: "none",
              boxShadow: "none",
              fontWeight: "bold",
            }}
          >
            {isExpanded ? "−" : "+"}
          </Button>
          {selectedSourceTypes.length === 0 ? (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="source-tooltip">
                  Please select at least one source type to search
                </Tooltip>
              }
            >
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  margin: 0,
                  color: "#dc3545",
                  cursor: "help",
                }}
              >
                SOURCE *
              </p>
            </OverlayTrigger>
          ) : (
            <p
              style={{
                fontSize: "14px",
                fontWeight: "bold",
                margin: 0,
              }}
            >
              SOURCE
            </p>
          )}
        </div>
        <Button
          variant="link"
          size="sm"
          style={{ padding: 0 }}
          onClick={handleSelectAll}
        >
          {selectedSourceTypes.length === availableSourceTypes.length
            ? "Deselect All"
            : "Select All"}
        </Button>
      </div>
      <div className={`${isExpanded ? "d-block" : "d-none"}`}>
        {availableSourceTypes.map((option) => (
          <div
            key={option.value}
            className="d-flex col-gap-1 align-items-center mb-1"
          >
            <div>
              <input
                type="checkbox"
                id={`source_type-${option.value}`}
                name="source_type"
                value={option.value}
                checked={selectedSourceTypes.includes(option.value)}
                onChange={handleSourceChange}
              />
              <label htmlFor={`source_type-${option.value}`}></label>
            </div>
            <label
              className="mb-0 ms-2 ml-2"
              htmlFor={`source_type-${option.value}`}
              style={{ flex: 1, fontSize: "13px" }}
            >
              {option.title}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SourceOptionsFilter;
